import React, { useState } from 'react';
import { Shuffle, Copy, Trash2, RefreshCw } from 'lucide-react';

const WordShufflerTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('words'); // words, letters, lines
  const [copied, setCopied] = useState(false);

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const shuffleText = () => {
    if (!input.trim()) {
      setOutput('');
      return;
    }

    let result = '';
    switch (mode) {
      case 'words':
        result = shuffleArray(input.split(/\s+/)).join(' ');
        break;
      case 'letters':
        result = input.split(' ').map(word => 
          shuffleArray(word.split('')).join('')
        ).join(' ');
        break;
      case 'lines':
        result = shuffleArray(input.split('\n')).join('\n');
        break;
      default:
        result = shuffleArray(input.split(/\s+/)).join(' ');
    }
    setOutput(result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'words', label: 'Shuffle Words' },
          { key: 'letters', label: 'Shuffle Letters' },
          { key: 'lines', label: 'Shuffle Lines' }
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Input Text
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter text to shuffle..."
          className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={shuffleText}
          disabled={!input.trim()}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
        >
          <Shuffle size={18} />
          Shuffle Text
        </button>
        <button
          onClick={shuffleText}
          disabled={!input.trim()}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          title="Shuffle Again"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Shuffled Output
          </label>
          <textarea
            value={output}
            readOnly
            className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white"
          />
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Result'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear All
        </button>
      </div>
    </div>
  );
};

export default WordShufflerTool;
